/**
 * Skat — server-authoritative rules engine (the German three-player trick game, one declarer against two defenders).
 *
 * Rules implemented (toggles live in {@link SkatConfig}):
 *  - 3 players, 32 cards (7 to ace): ten cards each and a two-card skat. The dealer sits rearhand; the player after the
 *    dealer is forehand and the next is middlehand.
 *  - Bidding on the standard ladder (18, 20, 22, 23, 24, 27, 30 ...): middlehand calls to forehand, the survivor then
 *    answers rearhand. The highest bid wins and becomes the declarer. If nobody bids, forehand may still play at 18;
 *    otherwise the hand is thrown in and redealt. After two throw-ins in a row forehand must play at 18.
 *  - The declarer picks up the skat, discards two cards (they count for the declarer's points) and names a game:
 *    a suit game (clubs 12, spades 11, hearts 10, diamonds 9), Grand (24, only the four jacks are trump) or Null
 *    (23, no trumps and the declarer must lose every trick; cards rank 7-8-9-10-J-Q-K-A).
 *  - In suit and Grand games the jacks are trumps (clubs, spades, hearts, diamonds) followed, in a suit game, by the
 *    trump suit's A, 10, K, Q, 9, 8, 7. Players must follow the led suit (a trump counts as its own suit).
 *  - Card points: A 11, 10 10, K 4, Q 3, J 2 (120 in the pack). The declarer needs 61 points to win.
 *  - Game value = base x (matadors + 1 + schneider + schwarz). Matadors are the unbroken run of top trumps the declarer
 *    holds ("with") or lacks ("without") among all twelve cards. Schneider (winner 90+, loser 30 or fewer) adds one and
 *    schwarz (all tricks, or none for the declarer) one more. Null is a flat 23.
 *  - Scoring: a win scores the game value; a loss scores minus twice the value. If the value is lower than the bid the
 *    declarer loses even when the cards were won, and the loss uses the next multiple of the base above the bid.
 *    Only the declarer scores; the defenders score nothing.
 *  - Simplifications: Hand (not looking at the skat), Ouvert, announced Schneider/Schwarz and Ramsch are omitted.
 *    A Null hand ends the moment the declarer takes a trick.
 *  - The match ends when a player reaches `targetScore` and leads, or after `maxHands` deals. Highest total wins.
 */
import { buildDeck, parseCard, removeCards, sortHand, SUITS, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface SkatConfig {
  /** Match ends when a player reaches this score and leads. */
  targetScore: number;
  /** Hard cap on deals so a match always terminates. */
  maxHands: number;
  handEndDelayMs: number;
  trickEndDelayMs: number;
}

export type SkatGame = Suit | 'GRAND' | 'NULL';
export const SKAT_GAMES: readonly SkatGame[] = ['C', 'S', 'H', 'D', 'GRAND', 'NULL'];

export type SkatPhase = 'BIDDING' | 'SKAT' | 'DECLARE' | 'PLAYING' | 'TRICK_END' | 'HAND_END' | 'MATCH_END';

export interface SkatTrickPlay {
  seat: number;
  card: CardId;
}

export interface SkatCompletedTrick {
  leader: number;
  plays: SkatTrickPlay[];
  winner: number;
}

/** One public bidding call. */
export interface SkatBidCall {
  seat: number;
  kind: 'bid' | 'hold' | 'pass';
  value?: number;
}

export interface SkatBidding {
  /** 1: middlehand calls forehand. 2: rearhand calls the stage-1 winner. 3: forehand may take the game at 18. */
  stage: 1 | 2 | 3;
  caller: number;
  responder: number;
  current: number;
  awaiting: 'caller' | 'responder';
  history: SkatBidCall[];
}

export interface SkatMatadors {
  with: boolean;
  count: number;
}

/** Scoring breakdown of a finished deal (public once the deal is over). */
export interface SkatHandResult {
  declarer: number;
  game: SkatGame;
  bid: number;
  matadors: SkatMatadors;
  /** The two cards the declarer put away. */
  discard: CardId[];
  declarerPoints: number;
  defenderPoints: number;
  declarerTricks: number;
  won: boolean;
  /** The game was worth less than the bid, so it is lost whatever the cards say. */
  overbid: boolean;
  schneider: boolean;
  schwarz: boolean;
  value: number;
  /** Points added to the declarer's match score (negative for a loss). */
  score: number;
}

export interface SkatState {
  config: SkatConfig;
  players: string[];
  hand: number;
  dealer: number;
  phase: SkatPhase;
  hands: CardId[][];
  skat: CardId[];
  bidding: SkatBidding;
  declarer: number | null;
  bid: number;
  game: SkatGame | null;
  discard: CardId[];
  matadors: SkatMatadors | null;
  turn: number;
  trick: SkatTrickPlay[];
  tricks: SkatCompletedTrick[];
  scores: number[];
  handResults: SkatHandResult[];
  /** Deals thrown in back to back; two in a row force forehand to play. */
  passedOut: number;
}

export type SkatAction =
  | { type: 'bid'; value: number }
  | { type: 'hold' }
  | { type: 'pass' }
  | { type: 'pickup' }
  | { type: 'declare'; game: SkatGame; discard: [CardId, CardId] }
  | { type: 'play'; card: CardId };

export type SkatLegal =
  | { kind: 'bid'; role: 'caller' | 'forehand'; current: number; values: number[] }
  | { kind: 'respond'; current: number }
  | { kind: 'pickup' }
  | { kind: 'declare'; hand: CardId[]; games: SkatGame[]; bid: number }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface SkatView {
  gameType: 'SKAT';
  config: SkatConfig;
  seat: number | null;
  phase: SkatPhase;
  round: number;
  dealer: number;
  forehand: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  /** Cards still lying in the skat (2 until the declarer picks it up). */
  skatCount: number;
  bidding: SkatBidding | null;
  declarer: number | null;
  bid: number;
  game: SkatGame | null;
  /** The declarer's discarded cards: visible to the declarer, and to everyone once the deal is over. */
  discard: CardId[];
  tricksWon: number[];
  /** Card points each seat has taken in tricks so far (public: the tricks are open). */
  trickPoints: number[];
  trick: SkatTrickPlay[];
  lastTrick: SkatCompletedTrick | null;
  tricks: SkatCompletedTrick[];
  scores: number[];
  handResults: SkatHandResult[];
  /** Match scores by seat (so generic score UIs work). */
  totals: number[];
  passedOut: number;
}

const DEFAULTS: SkatConfig = {
  targetScore: 100,
  maxHands: 12,
  handEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const SEATS = 3;
const TRICKS = 10;
export const SKAT_BASE: Record<SkatGame, number> = { C: 12, S: 11, H: 10, D: 9, GRAND: 24, NULL: 23 };
export const SKAT_NULL_VALUE = 23;
export const SKAT_MIN_BID = 18;

const CARD_POINTS: Record<number, number> = { 14: 11, 10: 10, 13: 4, 12: 3, 11: 2, 9: 0, 8: 0, 7: 0 };
const JACK_POWER: Record<Suit, number> = { C: 30, S: 29, H: 28, D: 27 };
/** Weakest to strongest outside Null (jacks are trumps and handled separately). */
const PLAIN_ORDER = [7, 8, 9, 12, 13, 10, 14];
const NULL_ORDER = [7, 8, 9, 10, 11, 12, 13, 14];

export const skatCardPoints = (card: CardId): number => CARD_POINTS[parseCard(card).rank] ?? 0;
export const skatForehand = (dealer: number): number => (dealer + 1) % SEATS;
export const skatMiddlehand = (dealer: number): number => (dealer + 2) % SEATS;
export const skatRearhand = (dealer: number): number => dealer % SEATS;

/** Every bid that can be made: base x multiplier for each game, plus Null's flat 23. */
export const SKAT_BID_LADDER: readonly number[] = (() => {
  const out = new Set<number>([SKAT_NULL_VALUE]);
  for (const base of [9, 10, 11, 12, 24]) for (let m = 2; m <= 18; m++) out.add(base * m);
  return [...out].sort((a, b) => a - b);
})();

export const skatBidsAbove = (current: number): number[] => SKAT_BID_LADDER.filter((v) => v > current);

export function skatIsTrump(card: CardId, game: SkatGame): boolean {
  if (game === 'NULL') return false;
  const c = parseCard(card);
  return c.rank === 11 || (game !== 'GRAND' && c.suit === game);
}

/** The suit a card counts as when following: trumps form their own "suit". */
export function skatEffectiveSuit(card: CardId, game: SkatGame): Suit | 'T' {
  return skatIsTrump(card, game) ? 'T' : parseCard(card).suit;
}

/** Comparable strength; only meaningful between cards of the same effective suit. */
export function skatPower(card: CardId, game: SkatGame): number {
  const c = parseCard(card);
  if (game === 'NULL') return NULL_ORDER.indexOf(c.rank);
  if (c.rank === 11) return 100 + JACK_POWER[c.suit];
  const base = PLAIN_ORDER.indexOf(c.rank);
  return skatIsTrump(card, game) ? 100 + base : base;
}

export function skatTrickWinner(plays: readonly SkatTrickPlay[], game: SkatGame): number {
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const ps = skatEffectiveSuit(p.card, game);
    const bs = skatEffectiveSuit(best.card, game);
    if (ps === bs ? skatPower(p.card, game) > skatPower(best.card, game) : ps === 'T') best = p;
  }
  return best.seat;
}

/** Cards a seat may play: follow the led (effective) suit when able. */
export function skatLegalCards(hand: readonly CardId[], trick: readonly SkatTrickPlay[], game: SkatGame): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = skatEffectiveSuit(trick[0]!.card, game);
  const follow = hand.filter((c) => skatEffectiveSuit(c, game) === lead);
  return follow.length > 0 ? follow : [...hand];
}

/** Matadors: the unbroken run of top trumps held ("with") or missing ("without"), counted over all twelve cards. */
export function skatMatadors(cards: readonly CardId[], game: SkatGame): SkatMatadors {
  if (game === 'NULL') return { with: true, count: 0 };
  const order: CardId[] = ['JC', 'JS', 'JH', 'JD'];
  if (game !== 'GRAND') for (const r of ['A', 'T', 'K', 'Q', '9', '8', '7']) order.push(`${r}${game}`);
  const withTop = cards.includes(order[0]!);
  let count = 0;
  for (const c of order) {
    if (cards.includes(c) !== withTop) break;
    count++;
  }
  return { with: withTop, count };
}

export function skatGameValue(game: SkatGame, matadors: number, schneider = false, schwarz = false): number {
  if (game === 'NULL') return SKAT_NULL_VALUE;
  return SKAT_BASE[game] * (1 + matadors + (schneider ? 1 : 0) + (schwarz ? 1 : 0));
}

/** Scores a finished deal for the declarer. */
export function skatScoreHand(args: {
  game: SkatGame;
  bid: number;
  matadors: SkatMatadors;
  /** Declarer card points including the discarded skat. */
  declarerPoints: number;
  declarerTricks: number;
}): Pick<SkatHandResult, 'won' | 'overbid' | 'schneider' | 'schwarz' | 'value' | 'score'> {
  const { game, bid } = args;
  const base = SKAT_BASE[game];
  let won: boolean;
  let schneider = false;
  let schwarz = false;
  if (game === 'NULL') {
    won = args.declarerTricks === 0;
  } else {
    won = args.declarerPoints >= 61;
    schneider = won ? args.declarerPoints >= 90 : args.declarerPoints <= 30;
    schwarz = won ? args.declarerTricks === TRICKS : args.declarerTricks === 0;
    if (schwarz) schneider = true;
  }
  const value = skatGameValue(game, args.matadors.count, schneider, schwarz);
  const overbid = value < bid;
  if (overbid) won = false;
  const lossValue = overbid ? Math.ceil(bid / base) * base : value;
  return { won, overbid, schneider, schwarz, value, score: won ? value : -2 * lossValue };
}

function newDeck(): CardId[] {
  return buildDeck().filter((c) => parseCard(c).rank >= 7);
}

function startBidding(dealer: number): SkatBidding {
  return { stage: 1, caller: skatMiddlehand(dealer), responder: skatForehand(dealer), current: 0, awaiting: 'caller', history: [] };
}

function deal(state: SkatState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(newDeck(), rng);
  const hands: CardId[][] = [[], [], []];
  for (let i = 0; i < 30; i++) hands[(state.dealer + 1 + (i % SEATS)) % SEATS]!.push(deck[i]!);
  state.hands = hands.map((h) => sortHand(h));
  state.skat = deck.slice(30);
  state.declarer = null;
  state.bid = 0;
  state.game = null;
  state.discard = [];
  state.matadors = null;
  state.trick = [];
  state.tricks = [];
  state.bidding = startBidding(state.dealer);
  state.phase = 'BIDDING';
  state.turn = state.bidding.caller;
  events.push({ type: 'dealt', hand: state.hand, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
  if (state.passedOut >= 2) {
    // Two throw-ins in a row: forehand has to play, at the minimum bid.
    declarerWins(state, skatForehand(state.dealer), SKAT_MIN_BID, events);
  }
}

function declarerWins(state: SkatState, seat: number, bid: number, events: EngineEvent[]): void {
  state.declarer = seat;
  state.bid = bid;
  state.passedOut = 0;
  state.phase = 'SKAT';
  state.turn = seat;
  events.push({ type: 'declarer', seat, bid });
}

function advanceBidding(state: SkatState, winner: number, events: EngineEvent[]): void {
  const b = state.bidding;
  if (b.stage === 1) {
    b.stage = 2;
    b.caller = skatRearhand(state.dealer);
    b.responder = winner;
    b.awaiting = 'caller';
    state.turn = b.caller;
    return;
  }
  if (b.current > 0) {
    declarerWins(state, winner, b.current, events);
    return;
  }
  // Nobody bid: forehand may still take the game at the minimum.
  b.stage = 3;
  b.caller = winner;
  b.responder = winner;
  b.awaiting = 'caller';
  state.turn = winner;
}

function pointsFor(state: SkatState): { declarerPoints: number; defenderPoints: number; declarerTricks: number } {
  const d = state.declarer!;
  let declarerPoints = state.discard.reduce((n, c) => n + skatCardPoints(c), 0);
  let defenderPoints = 0;
  let declarerTricks = 0;
  for (const t of state.tricks) {
    const pts = t.plays.reduce((n, p) => n + skatCardPoints(p.card), 0);
    if (t.winner === d) {
      declarerPoints += pts;
      declarerTricks++;
    } else defenderPoints += pts;
  }
  return { declarerPoints, defenderPoints, declarerTricks };
}

/** The cards the declarer ought to put away by default: the lowest-value cards that are not trumps. */
function defaultDiscard(hand: readonly CardId[], game: SkatGame): [CardId, CardId] {
  const keep = (c: CardId) => (skatIsTrump(c, game) ? 1000 : 0) + skatPower(c, game) + skatCardPoints(c) * 0.1;
  const sorted = [...hand].sort((a, b) => keep(a) - keep(b));
  return [sorted[0]!, sorted[1]!];
}

/** A safe, sensible game for a declarer who ran out of time: the suit with the most trumps. */
function defaultGame(hand: readonly CardId[]): SkatGame {
  let best: SkatGame = 'C';
  let bestN = -1;
  for (const s of SUITS) {
    const n = hand.filter((c) => skatIsTrump(c, s)).length;
    if (n > bestN) {
      best = s;
      bestN = n;
    }
  }
  return best;
}

export const skatEngine: GameEngine<SkatConfig, SkatState, SkatAction, SkatView, SkatLegal> = {
  gameType: 'SKAT',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 1, 5000);
    checkInt('maxHands', c.maxHands, 1, 50);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Skat needs exactly 3 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const dealer = rng.int(SEATS);
    const state: SkatState = {
      config,
      players,
      hand: 1,
      dealer,
      phase: 'BIDDING',
      hands: [],
      skat: [],
      bidding: startBidding(dealer),
      declarer: null,
      bid: 0,
      game: null,
      discard: [],
      matadors: null,
      turn: 0,
      trick: [],
      tricks: [],
      scores: [0, 0, 0],
      handResults: [],
      passedOut: 0,
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'pass') return { type: 'pass' };
    if (raw.type === 'hold') return { type: 'hold' };
    if (raw.type === 'pickup') return { type: 'pickup' };
    if (raw.type === 'bid' && typeof raw.value === 'number' && Number.isInteger(raw.value)) return { type: 'bid', value: raw.value };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    if (
      raw.type === 'declare' &&
      typeof raw.game === 'string' &&
      (SKAT_GAMES as readonly string[]).includes(raw.game) &&
      Array.isArray(raw.discard) &&
      raw.discard.length === 2 &&
      raw.discard.every((c) => typeof c === 'string' && c.length <= 4)
    ) {
      return { type: 'declare', game: raw.game as SkatGame, discard: [raw.discard[0] as CardId, raw.discard[1] as CardId] };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Skat action');
  },

  applyAction(state, seat, action, rng) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (state.phase === 'BIDDING') {
      const b = state.bidding;
      if (b.awaiting === 'caller') {
        if (action.type === 'bid') {
          const allowed = b.stage === 3 ? [SKAT_MIN_BID] : skatBidsAbove(b.current);
          assertEngine(allowed.includes(action.value), 'ILLEGAL_MOVE', 'That is not a higher bid on the ladder');
          b.current = action.value;
          b.history.push({ seat, kind: 'bid', value: action.value });
          events.push({ type: 'bid', seat, value: action.value });
          if (b.stage === 3) {
            declarerWins(state, seat, action.value, events);
            return events;
          }
          b.awaiting = 'responder';
          state.turn = b.responder;
          return events;
        }
        assertEngine(action.type === 'pass', 'INVALID_PHASE', 'Bid or pass');
        b.history.push({ seat, kind: 'pass' });
        events.push({ type: 'pass', seat });
        if (b.stage === 3) {
          // Nobody wants it: throw the hand in.
          state.passedOut++;
          state.dealer = (state.dealer + 1) % SEATS;
          events.push({ type: 'redeal', dealer: state.dealer });
          deal(state, rng, events);
          return events;
        }
        advanceBidding(state, b.responder, events);
        return events;
      }
      assertEngine(action.type === 'hold' || action.type === 'pass', 'INVALID_PHASE', 'Hold or pass');
      if (action.type === 'hold') {
        b.history.push({ seat, kind: 'hold', value: b.current });
        events.push({ type: 'hold', seat, value: b.current });
        b.awaiting = 'caller';
        state.turn = b.caller;
        return events;
      }
      b.history.push({ seat, kind: 'pass' });
      events.push({ type: 'pass', seat });
      advanceBidding(state, b.caller, events);
      return events;
    }

    if (state.phase === 'SKAT') {
      assertEngine(action.type === 'pickup', 'INVALID_PHASE', 'Pick up the skat');
      state.hands[seat]!.push(...state.skat);
      state.hands[seat] = sortHand(state.hands[seat]!);
      state.skat = [];
      state.phase = 'DECLARE';
      events.push({ type: 'skatPicked', seat });
      return events;
    }

    if (state.phase === 'DECLARE') {
      assertEngine(action.type === 'declare', 'INVALID_PHASE', 'Discard two cards and name a game');
      const [a, b] = action.discard;
      assertEngine(a !== b, 'INVALID_CARD', 'Discard two different cards');
      const hand = state.hands[seat]!;
      assertEngine(hand.includes(a) && hand.includes(b), 'INVALID_CARD', 'Card not in hand');
      state.matadors = skatMatadors(hand, action.game);
      removeCards(hand, [a, b]);
      state.discard = [a, b];
      state.game = action.game;
      state.phase = 'PLAYING';
      state.turn = skatForehand(state.dealer);
      events.push({ type: 'declared', seat, game: action.game, bid: state.bid });
      return events;
    }

    assertEngine(state.phase === 'PLAYING' && action.type === 'play', 'INVALID_PHASE', 'You must play a card');
    const game = state.game!;
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(skatLegalCards(hand, state.trick, game).includes(action.card), 'ILLEGAL_MOVE', 'You must follow suit');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = skatTrickWinner(state.trick, game);
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BIDDING' || state.phase === 'SKAT' || state.phase === 'DECLARE' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'BIDDING') {
      const b = state.bidding;
      if (b.awaiting === 'responder') return { kind: 'respond', current: b.current };
      if (b.stage === 3) return { kind: 'bid', role: 'forehand', current: 0, values: [SKAT_MIN_BID] };
      return { kind: 'bid', role: 'caller', current: b.current, values: skatBidsAbove(b.current) };
    }
    if (state.phase === 'SKAT') return { kind: 'pickup' };
    if (state.phase === 'DECLARE') return { kind: 'declare', hand: [...state.hands[seat]!], games: [...SKAT_GAMES], bid: state.bid };
    if (state.phase !== 'PLAYING') return { kind: 'none' };
    return { kind: 'play', cards: skatLegalCards(state.hands[seat]!, state.trick, state.game!) };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BIDDING') return { type: 'pass' };
    if (state.phase === 'SKAT') return { type: 'pickup' };
    const hand = state.hands[seat]!;
    if (state.phase === 'DECLARE') {
      const game = defaultGame(hand);
      return { type: 'declare', game, discard: defaultDiscard(hand, game) };
    }
    const game = state.game!;
    const legal = skatLegalCards(hand, state.trick, game);
    // Cheapest legal card by points, then strength: rarely wastes a winner and is never illegal.
    const lowest = [...legal].sort((a, b) => skatCardPoints(a) - skatCardPoints(b) || skatPower(a, game) - skatPower(b, game))[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    const over = state.phase === 'HAND_END' || state.phase === 'MATCH_END';
    const trickPoints = [0, 0, 0];
    const tricksWon = [0, 0, 0];
    for (const t of state.tricks) {
      tricksWon[t.winner]!++;
      trickPoints[t.winner]! += t.plays.reduce((n, p) => n + skatCardPoints(p.card), 0);
    }
    return {
      gameType: 'SKAT',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.hand,
      dealer: state.dealer,
      forehand: skatForehand(state.dealer),
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      skatCount: state.skat.length,
      bidding: state.phase === 'BIDDING' ? { ...state.bidding, history: state.bidding.history.map((h) => ({ ...h })) } : null,
      declarer: state.declarer,
      bid: state.bid,
      game: state.game,
      discard: (seat !== null && seat === state.declarer) || over ? [...state.discard] : [],
      tricksWon,
      trickPoints,
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      scores: [...state.scores],
      handResults: state.handResults.map((r) => ({ ...r, discard: [...r.discard], matadors: { ...r.matadors } })),
      totals: [...state.scores],
      passedOut: state.passedOut,
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'HAND_END') return { kind: 'nextHand', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      const last = state.tricks.at(-1)!;
      const game = state.game!;
      const nullBroken = game === 'NULL' && last.winner === state.declarer;
      if (state.tricks.length < TRICKS && !nullBroken) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const pts = pointsFor(state);
      const scored = skatScoreHand({ game, bid: state.bid, matadors: state.matadors!, declarerPoints: pts.declarerPoints, declarerTricks: pts.declarerTricks });
      state.scores[state.declarer!]! += scored.score;
      const result: SkatHandResult = {
        declarer: state.declarer!,
        game,
        bid: state.bid,
        matadors: state.matadors!,
        discard: [...state.discard],
        ...pts,
        ...scored,
      };
      state.handResults.push(result);
      events.push({ type: 'handEnd', hand: state.hand, result, scores: [...state.scores] });
      const cfg = state.config;
      const top = Math.max(...state.scores);
      const leaders = state.scores.filter((s) => s === top).length;
      const reached = top >= cfg.targetScore && leaders === 1;
      const capped = state.hand >= cfg.maxHands;
      if (reached || capped) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: standingsFromScores(state.scores.map((score, seat) => ({ seat, score }))) });
      } else {
        state.phase = 'HAND_END';
      }
      return events;
    }
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standingsFromScores(state.scores.map((score, seat) => ({ seat, score })));
  },

  getRoundsCompleted: (state) => state.handResults.length,
};
